if (!customElements.get('bulk-bundle-builder')) {
  customElements.define('bulk-bundle-builder', class BulkBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.querySelector('[data-products]')) return;
      this.initialized = true;
      this.max = Number(this.dataset.maxItems);
      this.min = Math.min(this.max, Number(this.dataset.minItems));
      this.key = this.dataset.storageKey;
      this.tiers = JSON.parse(this.querySelector('[data-tiers]').textContent)
        .filter(tier => tier.quantity <= this.max)
        .sort((a, b) => a.quantity - b.quantity);
      this.items = this.restore();
      this.cards = [...this.querySelectorAll('[data-product]')];
      this.reconcileCards();
      this.panel = this.querySelector('[data-panel]');
      this.querySelectorAll('[data-variant-picker]').forEach(picker => {
        const select = picker.querySelector('[data-variant]');
        const trigger = picker.querySelector('[data-variant-trigger]');
        select.labels?.[0]?.setAttribute('for', trigger.id);
        select.hidden = true;
        trigger.hidden = false;
        picker.classList.add('bulk-bundle__variant-picker--enhanced');
        this.syncPicker(picker);
      });
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-variant-trigger]');
        const choice = event.target.closest('[data-variant-option]');
        if (trigger && this.contains(trigger)) this.togglePicker(trigger.closest('[data-variant-picker]'));
        if (choice && this.contains(choice)) this.chooseVariant(choice);
        this.handleClick(event);
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant]')) {
          const picker = event.target.closest('[data-variant-picker]');
          if (picker) this.syncPicker(picker);
          this.render();
        }
        if (event.target.matches('[data-sort]')) this.sort(event.target.value);
      });
      this.addEventListener('keydown', event => this.handlePickerKeydown(event));
      this.addEventListener('focusout', event => {
        const picker = event.target.closest('[data-variant-picker]');
        if (picker && !picker.contains(event.relatedTarget)) this.closePicker(picker);
      });
      this.onOutsidePicker = event => {
        const openPicker = this.querySelector('[data-variant-picker].is-open');
        if (openPicker && !openPicker.contains(event.target)) this.closePicker(openPicker);
      };
      document.addEventListener('pointerdown', this.onOutsidePicker);
      this.render();
      this.onScroll = () => {
        const bounds = this.getBoundingClientRect();
        this.panel.classList.toggle('bulk-bundle__panel--visible', bounds.bottom > 0 && bounds.top < window.innerHeight);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('pointerdown', this.onOutsidePicker);
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.key));
        if (!Array.isArray(saved)) return new Map();
        let count = 0;
        const entries = saved.filter(item => {
          if (!item || !Number.isSafeInteger(Number(item.id)) || !Number.isSafeInteger(Number(item.quantity)) || !Number.isFinite(Number(item.price)) || Number(item.quantity) < 1 || count + Number(item.quantity) > this.max) return false;
          count += Number(item.quantity);
          return true;
        }).map(item => [String(item.id), {
          id: String(item.id), productId: String(item.productId), title: String(item.title),
          variant: String(item.variant), image: String(item.image), price: Number(item.price), quantity: Number(item.quantity)
        }]);
        return new Map(entries);
      } catch (_) {
        return new Map();
      }
    }

    reconcileCards() {
      this.cards.forEach(card => {
        [...card.querySelector('[data-variant]').options].forEach(option => {
          const item = this.items.get(option.value);
          if (!item) return;
          if (option.disabled || Number(option.dataset.limit) < item.quantity) {
            this.items.delete(option.value);
            return;
          }
          item.title = card.dataset.title;
          item.variant = option.dataset.title;
          item.image = option.dataset.image;
          item.price = Number(option.dataset.price);
        });
      });
      this.save();
    }

    save() {
      try { sessionStorage.setItem(this.key, JSON.stringify([...this.items.values()])); } catch (_) { /* Storage is optional. */ }
    }

    get count() {
      return [...this.items.values()].reduce((sum, item) => sum + item.quantity, 0);
    }

    format(cents) {
      const amount = Math.round(cents);
      if (window.theme?.Currency?.formatMoney && window.theme?.shopSettings?.moneyFormat) {
        return window.theme.Currency.formatMoney(amount, window.theme.shopSettings.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency }).format(amount / 100);
    }

    option(card) {
      const select = card.querySelector('[data-variant]');
      return select.options[select.selectedIndex];
    }

    syncPicker(picker) {
      const select = picker.querySelector('[data-variant]');
      const selected = select.options[select.selectedIndex];
      picker.querySelector('[data-variant-text]').textContent = selected?.textContent || '';
      picker.querySelectorAll('[data-variant-option]').forEach(choice => {
        choice.setAttribute('aria-selected', String(choice.dataset.variantValue === select.value));
      });
    }

    closePicker(picker) {
      if (!picker) return;
      picker.classList.remove('is-open', 'is-upward');
      picker.closest('[data-product]').classList.remove('is-variant-open');
      picker.querySelector('[data-variant-trigger]').setAttribute('aria-expanded', 'false');
      picker.querySelector('[data-variant-menu]').hidden = true;
    }

    togglePicker(picker, focusOption = false) {
      if (picker.classList.contains('is-open')) { this.closePicker(picker); return; }
      this.closePicker(this.querySelector('[data-variant-picker].is-open'));
      const trigger = picker.querySelector('[data-variant-trigger]');
      const menu = picker.querySelector('[data-variant-menu]');
      const rect = trigger.getBoundingClientRect();
      const visibleBottom = window.matchMedia('(max-width: 989px)').matches
        ? Math.min(window.innerHeight, this.panel.getBoundingClientRect().top)
        : window.innerHeight;
      const below = visibleBottom - rect.bottom - 8;
      const above = rect.top - 8;
      const desired = Math.min(240, menu.querySelectorAll('[data-variant-option]').length * 39 + 8);
      const upward = below < desired && above > below;
      picker.style.setProperty('--variant-menu-height', `${Math.max(40, upward ? above : below)}px`);
      picker.classList.toggle('is-upward', upward);
      picker.classList.add('is-open');
      picker.closest('[data-product]').classList.add('is-variant-open');
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      if (focusOption) {
        const selected = menu.querySelector('[aria-selected="true"]:not(:disabled)');
        (selected || menu.querySelector('[data-variant-option]:not(:disabled)'))?.focus();
      }
    }

    chooseVariant(choice) {
      if (choice.disabled) return;
      const picker = choice.closest('[data-variant-picker]');
      const select = picker.querySelector('[data-variant]');
      select.value = choice.dataset.variantValue;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.closePicker(picker);
      picker.querySelector('[data-variant-trigger]').focus();
    }

    handlePickerKeydown(event) {
      const picker = event.target.closest('[data-variant-picker]');
      if (!picker) return;
      if (event.key === 'Escape' && picker.classList.contains('is-open')) {
        event.preventDefault();
        this.closePicker(picker);
        picker.querySelector('[data-variant-trigger]').focus();
        return;
      }
      if (event.target.matches('[data-variant-trigger]')) {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (!picker.classList.contains('is-open')) this.togglePicker(picker);
          const choices = [...picker.querySelectorAll('[data-variant-option]:not(:disabled)')];
          const selectedIndex = choices.findIndex(choice => choice.getAttribute('aria-selected') === 'true');
          const index = event.key === 'Home' ? 0
            : event.key === 'End' ? choices.length - 1
              : event.key === 'ArrowUp' ? Math.max(0, selectedIndex - 1)
                : Math.min(choices.length - 1, selectedIndex + 1);
          choices[index]?.focus();
        }
        return;
      }
      if (!event.target.matches('[data-variant-option]')) return;
      const choices = [...picker.querySelectorAll('[data-variant-option]:not(:disabled)')];
      const index = choices.indexOf(event.target);
      let next;
      if (event.key === 'Home') next = choices[0];
      if (event.key === 'End') next = choices[choices.length - 1];
      if (event.key === 'ArrowDown') next = choices[(index + 1) % choices.length];
      if (event.key === 'ArrowUp') next = choices[(index - 1 + choices.length) % choices.length];
      if (next) { event.preventDefault(); next.focus(); }
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      error.textContent = message || '';
      error.hidden = !message;
    }

    handleClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button)) return;
      const action = button.dataset.action;
      if (action === 'details') {
        const details = this.querySelector('[data-details]');
        details.hidden = !details.hidden;
        button.setAttribute('aria-expanded', String(!details.hidden));
        return;
      }
      if (action === 'checkout') { this.checkout(); return; }
      if (action === 'remove') {
        const item = this.items.get(button.dataset.variantId);
        if (item?.quantity > 1) item.quantity -= 1;
        else this.items.delete(button.dataset.variantId);
        this.save();
        this.showError('');
        this.render();
        return;
      }
      const card = button.closest('[data-product]');
      if (!card) return;
      const option = this.option(card);
      if (!option || option.disabled) return;
      const id = option.value;
      const selected = this.items.get(id);
      if (action === 'decrease') {
        if (selected?.quantity > 1) selected.quantity -= 1;
        else this.items.delete(id);
      } else if (action === 'increase') {
        if (this.count >= this.max) { this.showError(this.dataset.errorLimit); return; }
        if ((selected?.quantity || 0) >= Number(option.dataset.limit)) { this.showError(this.dataset.errorInventory); return; }
        if (selected) selected.quantity += 1;
        else this.items.set(id, {
          id, productId: card.dataset.productId, title: card.dataset.title,
          variant: option.dataset.title, image: option.dataset.image,
          price: Number(option.dataset.price), quantity: 1
        });
      }
      this.save();
      this.showError('');
      this.render();
    }

    sort(order) {
      const container = this.querySelector('[data-products]');
      const cards = [...this.cards];
      cards.sort((a, b) => {
        if (order === 'price-asc' || order === 'price-desc') {
          const aPrice = Number(this.option(a).dataset.price), bPrice = Number(this.option(b).dataset.price);
          return order === 'price-asc' ? aPrice - bPrice : bPrice - aPrice;
        }
        if (order === 'title') return a.dataset.title.localeCompare(b.dataset.title);
        return Number(a.dataset.featuredOrder) - Number(b.dataset.featuredOrder);
      });
      container.append(...cards);
    }

    render() {
      const count = this.count;
      this.cards.forEach(card => {
        const selectedOption = this.option(card);
        const quantity = this.items.get(selectedOption.value)?.quantity || 0;
        card.querySelector('[data-quantity]').textContent = quantity;
        card.querySelector('[data-quantity-control]').hidden = quantity === 0;
        card.querySelector('[data-selected-mark]').hidden = quantity === 0;
        const addButton = card.querySelector('.bulk-bundle__add');
        addButton.hidden = quantity > 0;
        addButton.disabled = selectedOption.disabled || Number(selectedOption.dataset.limit) <= 0;
        const more = card.querySelector('[data-quantity-control] [data-action="increase"]');
        more.disabled = count >= this.max || quantity >= Number(selectedOption.dataset.limit);
        card.querySelector('[data-card-price]').textContent = this.format(Number(selectedOption.dataset.price));
      });
      this.querySelector('[data-count]').textContent = `${count} / ${this.max}`;

      const slots = this.querySelector('[data-slots]');
      slots.replaceChildren();
      const entries = [...this.items.values()].flatMap(item => Array.from({ length: item.quantity }, () => item));
      entries.forEach((item, index) => {
        const slot = document.createElement('div');
        slot.className = 'bulk-bundle__slot bulk-bundle__slot--filled';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        slot.append(image);
        const label = document.createElement('span');
        label.className = 'bulk-bundle__slot-label';
        label.textContent = item.title;
        slot.append(label);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bulk-bundle__remove';
        remove.dataset.action = 'remove';
        remove.dataset.variantId = item.id;
        remove.setAttribute('aria-label', `Remove ${item.title}`);
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.classList.add('icon');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('viewBox', '0 0 12 12');
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttribute('href', '#icon-close');
        icon.append(use);
        remove.append(icon);
        slot.append(remove);
        slots.append(slot);
      });
      for (let index = entries.length; index < this.max; index += 1) {
        const slot = document.createElement('div');
        slot.className = 'bulk-bundle__slot bulk-bundle__slot--empty';
        const number = document.createElement('span');
        number.className = 'bulk-bundle__slot-number';
        number.textContent = String(index + 1);
        const tier = this.tiers.find(value => value.quantity === index + 1);
        const caption = document.createElement('strong');
        caption.textContent = tier ? `${tier.percent}% OFF` : 'Select product';
        const bottom = document.createElement('span');
        bottom.className = 'bulk-bundle__slot-label';
        bottom.textContent = tier ? `${index + 1} items` : 'Select product';
        slot.append(number, caption, bottom);
        slots.append(slot);
      }

      const subtotal = [...this.items.values()].reduce((sum, item) => sum + item.price * item.quantity, 0);
      const tier = this.tiers.filter(value => count >= value.quantity).pop();
      const discounted = tier ? subtotal - Math.round(subtotal * tier.percent / 100) : subtotal;
      const saving = this.querySelector('[data-saving]');
      saving.hidden = !tier;
      if (tier) saving.textContent = `Est. save ${tier.percent}%`;
      const original = this.querySelector('[data-original]');
      original.hidden = !tier;
      original.textContent = this.format(subtotal);
      this.querySelector('[data-total]').textContent = this.format(discounted);
      this.querySelector('[data-estimate]').hidden = !tier;
      const details = this.querySelector('[data-details]');
      details.replaceChildren();
      this.items.forEach(item => {
        const line = document.createElement('div');
        const name = document.createElement('span');
        name.textContent = `${item.title}${item.variant !== 'Default Title' ? ` / ${item.variant}` : ''} x ${item.quantity}`;
        const price = document.createElement('span');
        price.textContent = this.format(item.price * item.quantity);
        line.append(name, price);
        details.append(line);
      });
      const submit = this.querySelector('[data-action="checkout"]');
      submit.disabled = count < this.min || this.busy;
      submit.textContent = count < this.min ? `Select ${this.min - count} more ${this.min - count === 1 ? 'item' : 'items'}` : 'Add bundle to cart';
    }

    async checkout() {
      if (this.busy) return;
      if (this.count < this.min) { this.showError(`Select at least ${this.min} items.`); return; }
      this.busy = true;
      this.showError('');
      this.render();
      try {
        const trackingTag = this.dataset.trackingTag?.trim();
        const items = [...this.items.values()].map(item => {
          const lineItem = { id: Number(item.id), quantity: item.quantity };
          if (trackingTag) lineItem.properties = { _bulk_bundle_tag: trackingTag };
          return lineItem;
        });
        if (trackingTag) {
          const attributesResponse = await fetch(window.theme?.routes?.cart_update_url || '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: { 'Bulk bundle tag': trackingTag } })
          });
          if (!attributesResponse.ok) throw new Error(this.dataset.errorCart);
        }
        const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorCart);
        this.items.clear();
        this.save();
        window.location.assign(window.theme?.routes?.cart_url || '/cart');
      } catch (error) {
        this.showError(error.message || this.dataset.errorCart);
        this.busy = false;
        this.render();
      }
    }
  });
}
